/**
 * Zentraler Zustand Store für CherryPulse
 * Mit Microsoft Entra ID Authentifizierung (MSAL)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Agency,
  Rating,
  UserInfo,
  AgencyType,
  EmployeeRange,
  RatingScores,
  RatingNotes,
  RatingOccasion,
  EvaluationLevel,
} from '@/types';
// Seed data for E2E test mode
import { SEED_AGENCIES, SEED_RATINGS } from '@/config/seed-data';
import type { ApiErrorResponse } from '@/types';
import { parseJsonResponse } from '@/lib/utils';

// Check if running in E2E test mode
const isTestMode = import.meta.env.VITE_TEST_MODE === 'true';

// ============================================
// STORAGE CLEANUP
// ============================================

// Alte localStorage-Keys entfernen (Daten kommen jetzt aus der Datenbank)
const LEGACY_STORAGE_KEYS = [
  'cherrypulse-storage-v6',
  'cherrypulse-storage-v5',
  'cherrypulse-storage-v4',
];

function cleanupLegacyStorage(): void {
  if (typeof window === 'undefined') return;
  for (const key of LEGACY_STORAGE_KEYS) {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      console.info(`[CherryPulse] Alte Storage-Daten entfernt: ${key}`);
    }
  }
}

// Cleanup beim Laden des Moduls ausführen
cleanupLegacyStorage();

// ============================================
// STATE INTERFACE
// ============================================

interface AppState {
  // Auth State
  user: UserInfo | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isDataLoading: boolean;
  authError: string | null;

  // Environment State
  activeEnvironment: 'production' | 'test';

  // Daten
  agencies: Agency[];
  ratings: Rating[];

  // UI State
  searchQuery: string;
  filterType: AgencyType | '';

  // Rating Flow State
  selectedAgencyId: string | null;
  ratingStep: number;
  ratingMode: 'selecting' | 'manual' | 'conversational';
  currentRating: {
    scores: Partial<RatingScores>;
    notes: Partial<RatingNotes>;
    overallNotes: string;
    occasion: RatingOccasion | null;
  };
  // Draft persistence
  draftTimestamp: number | null;
}

// ============================================
// ACTIONS INTERFACE
// ============================================

interface AppActions {
  // Auth Actions
  syncUserToBackend: (accessToken: string, profilePhoto?: string | null) => Promise<void>;
  fetchDataFromBackend: (accessToken: string) => Promise<void>;
  setAuthLoading: (isLoading: boolean) => void;
  setAuthError: (error: string | null) => void;
  logout: () => void;
  clearAuthError: () => void;

  // Environment Actions
  setActiveEnvironment: (env: 'production' | 'test') => void;
  toggleEnvironment: () => void;
  getEnvironmentHeaders: () => Record<string, string>;

  // Data Actions
  addAgency: (agency: Agency) => void;
  updateAgency: (agencyId: string, updates: Partial<Agency>) => void;
  removeAgency: (agencyId: string) => void;
  addRating: (rating: Rating) => void;
  updateRating: (ratingId: string, updates: Partial<Rating>) => void;
  setUser: (user: UserInfo | null) => void;

  // UI Actions
  setSearchQuery: (query: string) => void;
  setFilterType: (type: AgencyType | '') => void;

  // Rating Flow Actions
  startRating: (agencyId: string) => void;
  setRatingStep: (step: number) => void;
  setRatingMode: (mode: 'selecting' | 'manual' | 'conversational') => void;
  nextStep: () => void;
  previousStep: () => void;
  setOverallNotes: (notes: string) => void;
  setOccasion: (occasion: RatingOccasion) => void;
  setCurrentScores: (scores: Partial<RatingScores>) => void;
  saveRating: () => Rating | null;
  resetRating: () => void;
  // Draft persistence
  hasDraft: () => boolean;
  getDraftAgencyId: () => string | null;
  discardDraft: () => void;

  // Selectors (computed)
  getAgencyById: (id: string) => Agency | undefined;
  getRatingsByAgencyId: (agencyId: string) => Rating[];
  getLatestRatingForAgency: (agencyId: string) => Rating | undefined;
  getFilteredAgencies: () => Agency[];
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: AppState = {
  // Auth - MSAL handles auth state, store is secondary
  user: null,
  isAuthenticated: false,
  isAuthLoading: false,
  // In test mode, data is immediately available from seed data
  isDataLoading: !isTestMode,
  authError: null,

  // Environment - default to production
  activeEnvironment: 'production',

  // Data - use seed data in test mode, otherwise empty until fetched from DB
  agencies: isTestMode ? SEED_AGENCIES : [],
  ratings: isTestMode ? SEED_RATINGS : [],

  // UI
  searchQuery: '',
  filterType: '',
  selectedAgencyId: null,
  ratingStep: 0,
  ratingMode: 'selecting',
  currentRating: {
    scores: {},
    notes: {},
    overallNotes: '',
    occasion: null,
  },
  draftTimestamp: null,
};

// Draft expiration time: 24 hours
const DRAFT_EXPIRATION_MS = 24 * 60 * 60 * 1000;

// ============================================
// STORE
// ============================================

export const useAppStore = create<AppState & AppActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ============================================
        // AUTH ACTIONS
        // ============================================

        syncUserToBackend: async (accessToken: string, profilePhoto?: string | null) => {
          set({ isAuthLoading: true, authError: null }, false, 'syncUserToBackend/start');

          try {
            const envHeaders = get().getEnvironmentHeaders();
            const response = await fetch('/api/users/sync', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                ...envHeaders,
              },
              // Send profile photo if available (base64 data URL from MS Graph)
              body: profilePhoto ? JSON.stringify({ profilePhoto }) : undefined,
            });

            if (response.ok) {
              const userData = await parseJsonResponse<{
                id: string;
                displayName: string;
                email: string;
                avatar?: string;
              }>(response);
              set(
                {
                  user: {
                    id: userData.id,
                    displayName: userData.displayName,
                    email: userData.email,
                    avatar: userData.avatar,
                  },
                  isAuthenticated: true,
                  isAuthLoading: false,
                },
                false,
                'syncUserToBackend/success'
              );
            } else {
              const errorData = (await response
                .json()
                .catch(() => ({}))) as ApiErrorResponse;
              set(
                {
                  user: null,
                  isAuthenticated: false,
                  isAuthLoading: false,
                  authError: errorData.error?.message || 'Benutzer-Synchronisation fehlgeschlagen',
                },
                false,
                'syncUserToBackend/error'
              );
            }
          } catch (error) {
            console.error('Failed to sync user to backend:', error);
            set(
              {
                user: null,
                isAuthenticated: false,
                isAuthLoading: false,
                authError: 'Netzwerkfehler bei der Benutzer-Synchronisation',
              },
              false,
              'syncUserToBackend/networkError'
            );
          }
        },

        setAuthLoading: (isAuthLoading: boolean) =>
          set({ isAuthLoading }, false, 'setAuthLoading'),

        setAuthError: (authError: string | null) =>
          set({ authError }, false, 'setAuthError'),

        logout: () => {
          // Clear local state - MSAL handles the actual logout
          set(
            {
              user: null,
              isAuthenticated: false,
              authError: null,
            },
            false,
            'logout'
          );
        },

        clearAuthError: () => set({ authError: null }, false, 'clearAuthError'),

        // ============================================
        // ENVIRONMENT ACTIONS
        // ============================================

        setActiveEnvironment: (env: 'production' | 'test') => {
          set({ activeEnvironment: env }, false, 'setActiveEnvironment');
        },

        toggleEnvironment: () => {
          const current = get().activeEnvironment;
          const newEnv = current === 'production' ? 'test' : 'production';
          set({ activeEnvironment: newEnv }, false, 'toggleEnvironment');
        },

        getEnvironmentHeaders: (): Record<string, string> => {
          const env = get().activeEnvironment;
          if (env === 'test') {
            return { 'X-Environment': 'test' };
          }
          return {};
        },

        fetchDataFromBackend: async (accessToken: string) => {
          set({ isDataLoading: true }, false, 'fetchDataFromBackend/start');
          try {
            const envHeaders = get().getEnvironmentHeaders();
            // Fetch agencies and ratings in parallel
            // Use cache: 'no-store' to bypass browser and CDN cache (important for environment switching)
            const [agenciesRes, ratingsRes] = await Promise.all([
              fetch('/api/agencies', {
                headers: { 'Authorization': `Bearer ${accessToken}`, ...envHeaders },
                cache: 'no-store',
              }),
              fetch('/api/ratings', {
                headers: { 'Authorization': `Bearer ${accessToken}`, ...envHeaders },
                cache: 'no-store',
              }),
            ]);

            if (agenciesRes.ok && ratingsRes.ok) {
              // API returns { agencies: [...], total, limit, offset } and { ratings: [...] }
              // API uses snake_case and lowercase type names, need to transform
              interface ApiAgency {
                id: string;
                name: string;
                type: string;
                city: string;
                website?: string;
                logo?: string;
                employees?: EmployeeRange;
                founded?: number;
                specialties?: string[];
                description?: string;
                contactEmail?: string;
                contactPhone?: string;
                createdAt?: string;
                updatedAt?: string;
                createdBy?: string;
              }

              const agenciesResponse = await parseJsonResponse<{ agencies: ApiAgency[] }>(agenciesRes);
              const ratingsResponse = await parseJsonResponse<{ ratings: Rating[] }>(ratingsRes);

              // Map database type names back to frontend type names
              const typeMap: Record<string, AgencyType> = {
                'creative': 'Kreativ',
                'media': 'Media',
                'digital': 'Digital',
                'pr': 'PR',
                'event': 'Event',
                'fullservice': 'Other',
              };

              // Transform API response to match frontend Agency type
              // Generate favicon URL from website if available
              // Uses DuckDuckGo's icon service (reliable, no 404s)
              const getFaviconUrl = (website?: string): string | undefined => {
                if (!website) return undefined;
                try {
                  const url = new URL(website);
                  return `https://icons.duckduckgo.com/ip3/${url.hostname}.ico`;
                } catch {
                  return undefined;
                }
              };

              const agencies: Agency[] = (agenciesResponse.agencies || []).map((a) => ({
                id: a.id,
                partitionKey: a.type,
                name: a.name,
                type: typeMap[a.type] || 'Other',
                city: a.city,
                website: a.website,
                logo: a.logo || getFaviconUrl(a.website),
                employees: a.employees,
                founded: a.founded,
                specialties: a.specialties,
                description: a.description,
                contactEmail: a.contactEmail,
                contactPhone: a.contactPhone,
                createdAt: a.createdAt || new Date().toISOString(),
                updatedAt: a.updatedAt || new Date().toISOString(),
                createdBy: a.createdBy || '',
              }));

              const ratings = ratingsResponse.ratings || [];

              set(
                {
                  agencies,
                  ratings,
                  isDataLoading: false,
                },
                false,
                'fetchDataFromBackend/success'
              );
            } else {
              set({ isDataLoading: false }, false, 'fetchDataFromBackend/error');
            }
          } catch {
            set({ isDataLoading: false }, false, 'fetchDataFromBackend/error');
          }
        },

        // ============================================
        // DATA ACTIONS
        // ============================================

        addAgency: (agency) =>
          set(
            (state) => ({ agencies: [...state.agencies, agency] }),
            false,
            'addAgency'
          ),

        updateAgency: (agencyId, updates) =>
          set(
            (state) => ({
              agencies: state.agencies.map((a) =>
                a.id === agencyId ? { ...a, ...updates } : a
              ),
            }),
            false,
            'updateAgency'
          ),

        removeAgency: (agencyId) =>
          set(
            (state) => ({
              agencies: state.agencies.filter((a) => a.id !== agencyId),
              ratings: state.ratings.filter((r) => r.agencyId !== agencyId),
            }),
            false,
            'removeAgency'
          ),

        addRating: (rating) =>
          set(
            (state) => ({ ratings: [...state.ratings, rating] }),
            false,
            'addRating'
          ),

        updateRating: (ratingId, updates) =>
          set(
            (state) => ({
              ratings: state.ratings.map((r) =>
                r.id === ratingId ? { ...r, ...updates } : r
              ),
            }),
            false,
            'updateRating'
          ),

        setUser: (user) =>
          set(
            { user, isAuthenticated: !!user },
            false,
            'setUser'
          ),

        // ============================================
        // UI ACTIONS
        // ============================================

        setSearchQuery: (searchQuery) =>
          set({ searchQuery }, false, 'setSearchQuery'),

        setFilterType: (filterType) =>
          set({ filterType }, false, 'setFilterType'),

        // ============================================
        // RATING FLOW ACTIONS
        // ============================================

        startRating: (agencyId) =>
          set(
            {
              selectedAgencyId: agencyId,
              ratingStep: 0,
              ratingMode: 'selecting',
              currentRating: {
                scores: {},
                notes: {},
                overallNotes: '',
                occasion: null,
              },
              draftTimestamp: Date.now(),
            },
            false,
            'startRating'
          ),

        setRatingStep: (ratingStep) =>
          set({ ratingStep, draftTimestamp: Date.now() }, false, 'setRatingStep'),

        setRatingMode: (ratingMode) =>
          set({ ratingMode, draftTimestamp: Date.now() }, false, 'setRatingMode'),

        nextStep: () =>
          set(
            (state) => ({ ratingStep: state.ratingStep + 1, draftTimestamp: Date.now() }),
            false,
            'nextStep'
          ),

        previousStep: () =>
          set(
            (state) => ({ ratingStep: Math.max(0, state.ratingStep - 1), draftTimestamp: Date.now() }),
            false,
            'previousStep'
          ),

        setOverallNotes: (overallNotes) =>
          set(
            (state) => ({
              currentRating: {
                ...state.currentRating,
                overallNotes,
              },
              draftTimestamp: Date.now(),
            }),
            false,
            'setOverallNotes'
          ),

        setOccasion: (occasion) =>
          set(
            (state) => ({
              currentRating: {
                ...state.currentRating,
                occasion,
              },
              draftTimestamp: Date.now(),
            }),
            false,
            'setOccasion'
          ),

        setCurrentScores: (scores) =>
          set(
            (state) => ({
              currentRating: {
                ...state.currentRating,
                scores,
              },
              draftTimestamp: Date.now(),
            }),
            false,
            'setCurrentScores'
          ),

        saveRating: () => {
          const state = get();
          const { selectedAgencyId, currentRating, user } = state;

          if (!selectedAgencyId || !user) return null;

          // Prüfen ob mindestens ein Score pro Ebene vorhanden ist (neues 3-Ebenen-Format)
          const scores = currentRating.scores;
          const levels: EvaluationLevel[] = ['agency', 'team', 'chemistry'];

          const hasMinimumScores = levels.every((level) => {
            const levelScores = scores[level];
            if (!levelScores) return false;
            // Mindestens ein Hauptkriterium muss bewertet sein
            const mainScores = Object.values(levelScores.main || {}).filter(v => v && v > 0);
            return mainScores.length > 0;
          });
          if (!hasMinimumScores) return null;

          const now = new Date();
          const newRating: Rating = {
            id: `rating-${Date.now()}`,
            partitionKey: selectedAgencyId,
            agencyId: selectedAgencyId,
            userId: user.id,
            scores: scores as RatingScores,
            notes: currentRating.notes,
            overallNotes: currentRating.overallNotes || undefined,
            createdAt: now.toISOString(),
            period: now.toISOString().slice(0, 7),
            source: 'manual',
          };

          set(
            (state) => ({
              ratings: [...state.ratings, newRating],
              selectedAgencyId: null,
              ratingStep: 0,
              ratingMode: 'selecting',
              currentRating: {
                scores: {},
                notes: {},
                overallNotes: '',
                occasion: null,
              },
              draftTimestamp: null,
            }),
            false,
            'saveRating'
          );

          return newRating;
        },

        resetRating: () =>
          set(
            {
              selectedAgencyId: null,
              ratingStep: 0,
              ratingMode: 'selecting',
              currentRating: {
                scores: {},
                notes: {},
                overallNotes: '',
                occasion: null,
              },
              draftTimestamp: null,
            },
            false,
            'resetRating'
          ),

        // Draft persistence helpers
        hasDraft: () => {
          const state = get();
          // Check if there's a valid draft (not expired and has some content)
          if (!state.draftTimestamp || !state.selectedAgencyId) return false;
          const isExpired = Date.now() - state.draftTimestamp > DRAFT_EXPIRATION_MS;
          if (isExpired) return false;
          // Check if there's actual content (scores or notes)
          const hasScores = Object.keys(state.currentRating.scores).length > 0;
          const hasNotes = Object.keys(state.currentRating.notes).length > 0 ||
                          state.currentRating.overallNotes.length > 0;
          const hasProgress = state.ratingStep > 0;
          return hasScores || hasNotes || hasProgress;
        },

        getDraftAgencyId: () => {
          const state = get();
          if (get().hasDraft()) {
            return state.selectedAgencyId;
          }
          return null;
        },

        discardDraft: () =>
          set(
            {
              selectedAgencyId: null,
              ratingStep: 0,
              ratingMode: 'selecting',
              currentRating: {
                scores: {},
                notes: {},
                overallNotes: '',
                occasion: null,
              },
              draftTimestamp: null,
            },
            false,
            'discardDraft'
          ),

        // ============================================
        // SELECTORS
        // ============================================

        getAgencyById: (id) => {
          return get().agencies.find((a) => a.id === id);
        },

        getRatingsByAgencyId: (agencyId) => {
          return get()
            .ratings.filter((r) => r.agencyId === agencyId)
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
        },

        getLatestRatingForAgency: (agencyId) => {
          const ratings = get().getRatingsByAgencyId(agencyId);
          return ratings[0];
        },

        getFilteredAgencies: () => {
          const { agencies, searchQuery, filterType } = get();
          return agencies
            .filter((agency) => {
              const matchesSearch =
                !searchQuery ||
                agency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                agency.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
                agency.type.toLowerCase().includes(searchQuery.toLowerCase());

              const matchesType = !filterType || agency.type === filterType;

              return matchesSearch && matchesType;
            })
            .sort((a, b) => a.name.localeCompare(b.name, 'de'));
        },
      }),
      {
        name: 'cherrypulse-storage-v7',
        partialize: (state) => ({
          // NICHT persistieren: agencies, ratings - diese kommen aus der Datenbank!
          // Nur Draft-Persistenz für unterbrochene Bewertungen
          selectedAgencyId: state.selectedAgencyId,
          ratingStep: state.ratingStep,
          ratingMode: state.ratingMode,
          currentRating: state.currentRating,
          draftTimestamp: state.draftTimestamp,
          // Environment-Einstellung persistieren
          activeEnvironment: state.activeEnvironment,
        }),
      }
    ),
    { name: 'CherryPulse' }
  )
);
